import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useUserContext} from "../../../types/contexts/UserContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import DropdownInput from "../../../components/DropdownInput";
import CountryAvatar from "../../../components/CountryAvatar";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import {Region} from "../../../types/Responses/RegionsResponse";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {setSidebarTitle} from "../../../reducers/sidebar";

const RegionForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [name, setName] = useState<string>();
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [countries, setCountries] = useState<number[]>([]);
    const [allCountries, setAllCountries] = useState<CountriesShortResponse>();

    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);

    const changeName = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setName(e.currentTarget.value)
        }
    }

    const changeCountries = useCallback((s: string | number) => {
        const newCountries = countries ? [...countries] : [];
        setCountries([...newCountries, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }));
    }, [setCountries, countries]);

    const onCountryRemove = useCallback((index: number) => {
        if (countries) {
            const newCountries = [...countries];
            if (newCountries[index]) {
                newCountries.splice(index, 1)
            }
            setCountries(newCountries)
        }
    }, [countries, setCountries]);

    const handleSuccessResponse = (response: AxiosResponse, message: JSX.Element | string) => {
        const region = response.data.data as Region
        if (region) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/regions');
        }
    }

    const callback = (b: boolean) => {
        setValidated(true);
        setInProgress(false);

        if (b) {
            const request = {name, countries};
            if (id) {
                API.updateRegion(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.regions.updated.title', {name}))).catch(e => ErrorHandler(e, `put${APILink.GET_REGION.replace('$region', id.toString())}`, toast, handleError));
            } else {
                API.createRegion(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/regions'));
                    handleSuccessResponse(response, t('admin.regions.created.title', {name}));
                }).catch(e => ErrorHandler(e,`post${APILink.GET_REGIONS}`, toast, handleError));
            }

        }
    }

    const submit = () => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {key: 'name', label: t('admin.regions.table.name'), type: InputType.text, regex: '^.{3,}\\b$'},
                ],
                introducedData: {name, countries},
                callback: callback
            }
        }));
    }
    const getSingleRegion = useCallback(() => {
        API.getRegion(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const regionResponse = response.data.data as Region
            setName(regionResponse.name);
            setCountries(regionResponse.countries);
            setFormIsReady(true);
            setTitle(t('admin.regions.update.title', {name: regionResponse.name}));
            dispatch(setSidebarTitle(t('admin.regions.update.title', {name: regionResponse.name})));
        }).catch(e => {
            ErrorHandler(e, `get${APILink.GET_REGION.replace('$region', id?.toString() ?? '')}`, toast, handleError, setIsForbidden)
            setFormIsReady(true);
        });
    }, [setName, setCountries, setFormIsReady, toast, handleError, id]);

    const getAllCountries = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            setAllCountries(response.data as CountriesShortResponse);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast]);

    useEffect(() => {
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                if (id) {
                    getSingleRegion();
                } else {
                    setTitle(t('admin.regions.create.title'));
                    dispatch(setSidebarTitle(t('admin.regions.create.title')));
                    setFormIsReady(true);
                    if (user && !user.user.permissions.includes('create region')) {
                        setIsForbidden(true);
                    }
                }
                getAllCountries();
            }).catch()
        }
        dispatch(clear())
    }, [dispatch, id, user, t]);

    const countriesUI = useMemo(() => {
        if (!allCountries)
            return <></>;

        return <>
            <div className="max-w-md mt-4">
                <DropdownInput id="countries_form" name={t('admin.regions.select_countries')}
                               searchable={true}
                               multiple={true}
                               withLabel={false}
                               selected={countries}
                               onChange={changeCountries}
                               onRemove={onCountryRemove}
                               buttonClassName="min-h-[auto!important]"
                               list={allCountries.data.map((item) => {
                                   return {
                                       id: item.id,
                                       name: item.name,
                                       icon: <CountryAvatar country={item} withName={false}
                                                            className="h-3 max-w-[1rem]"/>
                                   }
                               }) ?? []}/>

            </div>
            <h3 className="font-bold text-md my-8">{t('admin.regions.countries')}</h3>
            <div className="w-full mt-1 grid grid-cols-3">
                {countries.map((item) => {
                    const country = allCountries.data.find(i => i.id === item);
                    if (!country) return <></>
                    return <div key={country.id}><CountryAvatar key={country.name} country={country}
                                                                className="h-6 max-w-[2rem] mb-2"/></div>
                })}
            </div>
        </>
    }, [countries, allCountries, changeCountries, onCountryRemove]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden && <>
                            <div className="max-w-md">
                                <Input success={!hasError('name', errors) && validated}
                                       error={validated && hasError('name', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.regions.table.name')} type={'text'}
                                       onChange={changeName}
                                       value={name}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('name', errors) &&
                                    <label key={'error-name'}
                                           className="mt-1 text-red-500 text-xs block">{getError('name', errors)}</label>}
                            </div>

                            {countriesUI}

                            <div className="mt-12">
                                <Button onClick={() => submit()} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </>}
                        <Forbidden isForbidden={isForbidden}/>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(RegionForm);
